import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useTranslation } from 'react-i18next';
import { roundImage, getTaskStatus } from '../../services/chatApi';
import { Download, Upload, Circle, Square, RefreshCw, Loader2, LayoutTemplate } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { dataURLtoFile } from '../../utils/imageUtils';
import ToolNavigator from '../../components/ToolNavigator';
import useImageTransfer from '../../hooks/useImageTransfer';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};



// Helper for circular crop initialization
function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 50,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  )
}

const RoundImage = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    const location = useLocation();
    
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [processedImage, setProcessedImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const pollInterval = useRef(null);
    // Controls
    const [mode, setMode] = useState('corners'); // 'corners' | 'circular'
    const [radius, setRadius] = useState(20); // For corners
    
    // For Circular Mode (ReactCrop)
    const [crop, setCrop] = useState();
    const imgRef = useRef(null);

    // Handle Navigation
    const resetParams = useCallback(() => {
        setMode('corners');
        setRadius(20);
        setCrop(undefined);
    }, []);

    // Handle incoming image transfers
    useImageTransfer(setFile, setPreviewUrl, setProcessedImage, resetParams);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => clearInterval(pollInterval.current);
    }, []);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setPreviewUrl(URL.createObjectURL(selectedFile));
            setProcessedImage(null);
            resetParams();
        }
    };

    const onImageLoad = (e) => {
        if (mode === 'circular') {
            const { width, height } = e.currentTarget;
            setCrop(centerAspectCrop(width, height, 1));
        }
    };

    // Switch modes logic
    useEffect(() => {
        if (mode === 'circular' && imgRef.current && !crop) {
            const { width, height } = imgRef.current;
            setCrop(centerAspectCrop(width, height, 1));
        }
    }, [mode, crop]);


    const handleProcess = async () => {
        if (!file) return;
        
        setIsLoading(true);
        setError(null);
        setProcessedImage(null);
        
        try {
            let params = { mode };

            if (mode === 'corners') {
                params.radius = radius;
            } else {
                if (!crop || !imgRef.current) return;
                
                // Calculate scale
                const image = imgRef.current;
                const scaleX = image.naturalWidth / image.width;
                const scaleY = image.naturalHeight / image.height;

                params.x = crop.x * scaleX;
                params.y = crop.y * scaleY;
                params.size = crop.width * scaleX; // Width = Height for circles
            }

            const startResponse = await roundImage(file, params);
            const taskId = startResponse.task_id;

            if (!taskId) throw new Error("No Task ID returned");

            // 3. Poll for Completion
            pollInterval.current = setInterval(async () => {
                try {
                    const statusRes = await getTaskStatus(taskId);
                    const status = statusRes.status || statusRes.state;

                    if (status === 'SUCCESS' || status === 'completed') {
                        clearInterval(pollInterval.current);
                        
                        const result = statusRes.result || statusRes.info;
                        if (result.new_credits !== undefined) {
                            setCredits(result.new_credits);
                        }
                        // We expect { download_url: "..." } from the backend
                        if (result.download_url) {
                            setProcessedImage(result.download_url);
                            setIsLoading(false);
                        } else {
                            // Fallback if structure is different
                            setError("Invalid response format from server.");
                            setIsLoading(false);
                        }

                    } else if (status === 'FAILURE' || status === 'failed') {
                        clearInterval(pollInterval.current);
                        setIsLoading(false);
                        setError(t('roundImage.errors.generic'));
                        console.error("Task Failed:", statusRes.result);
                    }
                } catch (pollErr) {
                    clearInterval(pollInterval.current);
                    setIsLoading(false);
                    setError("Network error while checking status.");
                }
            }, 2000); // Poll every 2 seconds
        } catch (err) {
            setError(t('roundImage.errors.generic'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!processedImage) return;
        try {
            // Fetch blob to ensure download behavior
            const response = await fetch(processedImage);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            
            const link = document.createElement('a');
            link.href = url;
            link.download = mode === 'corners' ? "rounded_corners.png" : "circle_crop.png";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            // Fallback
            window.open(processedImage, '_blank');
        }
    };

    const resetTool = () => {
        setFile(null);
        setPreviewUrl(null);
        setProcessedImage(null);
        setCrop(undefined);
        setMode('corners');
        setRadius(20);
	clearInterval(pollInterval.current);
    };


    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
        {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2))? (
                <SubscribePrompt />
            ) : (
            <div className="max-w-6xl mx-auto">
                <h3 className="text-3xl font-bold text-brand-orange mb-2">
                    {t('roundImage.title')}
                </h3>
                <p className="text-slate-600 mb-8 max-w-2xl">
                    {t('roundImage.description')}
                </p>

                {!file ? (
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-16 text-center hover:border-brand-orange transition-colors bg-slate-50">
                        <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" id="round-upload" />
                        <label htmlFor="round-upload" className="cursor-pointer flex flex-col items-center">
                            <div className="bg-white p-4 rounded-full shadow-sm mb-4">
                                <Upload size={32} className="text-brand-orange" />
                            </div>
                            <span className="text-xl font-medium text-slate-700 mb-2">{t('roundImage.upload.label')}</span>
                            <span className="text-sm text-slate-500">{t('roundImage.upload.hint')}</span>
                        </label>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        
                        {/* Left Column: Preview Area */}
                        <div className="lg:col-span-8">
                            <div className="bg-slate-100 p-4 rounded-lg border border-slate-200 flex justify-center items-center min-h-[400px]">
                                {processedImage ? (
                                    <div className="relative">
                                        {/* Checkerboard background for transparency visibility */}
                                        <div className="absolute inset-0 bg-brand-white bg-cover opacity-20 z-0"></div>
                                        <img src={processedImage} alt="Result" className="relative z-10 max-h-[500px] w-auto" />
                                    </div>
                                ) : (
                                    mode === 'corners' ? (
                                        // Rounded Corners Preview (CSS approximated)
                                        <img 
                                            src={previewUrl} 
                                            alt="Preview" 
                                            className="max-h-[500px] w-auto shadow-lg transition-all duration-200"
                                            style={{ borderRadius: `${radius}px` }}
                                        />
                                    ) : (
                                        // Circular Crop Interface
                                        <ReactCrop 
                                            crop={crop} 
                                            onChange={(c) => setCrop(c)} 
                                            circularCrop 
                                            aspect={1}
                                            keepSelection
                                        >
                                            <img 
                                                ref={imgRef}
                                                src={previewUrl} 
                                                alt="Circular Editor" 
                                                onLoad={onImageLoad}
                                                className="max-h-[500px] w-auto" 
                                            />
                                        </ReactCrop>
                                    )
                                )}
                            </div>

                            {/* Tool Navigator */}
                            {processedImage && (
                                <div className="mt-4">
                                    <ToolNavigator currentImage={processedImage} />
                                </div>
                            )}
                            
                            {error && <div className="mt-4 text-red-600 bg-red-50 p-3 rounded">{error}</div>}
                        </div>

                        {/* Right Column: Controls */}
                        <div className="lg:col-span-4 space-y-6">
                            <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                                <h4 className="font-semibold mb-4 flex items-center gap-2 text-brand-orange">
                                    <LayoutTemplate size={20} /> {t('roundImage.controls.title')}
                                </h4>

                                {!processedImage ? (
                                    <>
                                        {/* Mode Selection */}
                                        <div className="grid grid-cols-2 gap-2 mb-6">
                                            <button 
                                                onClick={() => setMode('corners')}
                                                className={`p-2 rounded-md border text-sm font-medium flex flex-col items-center gap-1 transition ${mode === 'corners' ? 'bg-orange-50 border-brand-orange text-brand-orange' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                                            >
                                                <Square size={18} className="rounded-md" /> {t('roundImage.modes.corners')}
                                            </button>
                                            <button 
                                                onClick={() => setMode('circular')}
                                                className={`p-2 rounded-md border text-sm font-medium flex flex-col items-center gap-1 transition ${mode === 'circular' ? 'bg-orange-50 border-brand-orange text-brand-orange' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                                            >
                                                <Circle size={18} /> {t('roundImage.modes.circular')}
                                            </button>
                                        </div>

                                        {/* Specific Controls */}
                                        {mode === 'corners' ? (
                                            <div className="mb-6">
                                                <div className="flex justify-between mb-2">
                                                    <label className="text-sm font-medium text-slate-700">{t('roundImage.controls.radius')}</label>
                                                    <span className="text-sm text-slate-500">{radius}px</span>
                                                </div>
                                                <input 
                                                    type="range" 
                                                    min="0" 
                                                    max="200" 
                                                    value={radius} 
                                                    onChange={(e) => setRadius(parseInt(e.target.value))} 
                                                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-brand-orange" 
                                                />
                                            </div>
                                        ) : (
                                            <div className="mb-6 p-3 bg-blue-50 rounded text-sm text-blue-800 border border-blue-100">
                                                💡 {t('roundImage.controls.dragHint')}
                                            </div>
                                        )}

                                        <div className="pt-4 border-t border-slate-100">
                                            <button 
                                                onClick={handleProcess} 
                                                disabled={isLoading} 
                                                className="w-full bg-brand-orange text-white py-3 rounded-md hover:bg-orange-600 transition flex items-center justify-center gap-2 font-semibold disabled:opacity-70"
                                            >
                                                {isLoading ? <Loader2 className="animate-spin" /> : <Circle />} 
                                                {t('roundImage.buttons.apply')}
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="space-y-3">
                                        <button onClick={handleDownload} className="w-full bg-green-600 text-white py-3 rounded-md hover:bg-green-700 transition flex items-center justify-center gap-2 font-semibold">
                                            <Download size={18} /> {t('roundImage.buttons.download')}
                                        </button>
                                        <button onClick={() => setProcessedImage(null)} className="w-full bg-slate-200 text-slate-700 py-3 rounded-md hover:bg-slate-300 transition flex items-center justify-center gap-2">
                                            <LayoutTemplate size={18} /> {t('roundImage.buttons.edit')}
                                        </button>
                                        <button onClick={resetTool} className="w-full bg-slate-100 text-slate-700 py-3 rounded-md hover:bg-slate-200 transition flex items-center justify-center gap-2">
                                            <RefreshCw size={18} /> {t('roundImage.buttons.newImage')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            )}
        </div>
    );
};

export default RoundImage;
